import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { closeChat, llmSelectors, minimizeChat, openChat } from 'app-components/flux/llmInterface';
import { DraggablePopper } from 'components/DraggablePopper';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import React, { useRef, useState } from 'react';
import { tracker } from 'app-components/utils/core';
import { trackingEvents } from 'containers/SimplifiAiAssistant/AiAssistant/aiEvents';
import { useChatDock } from './ChatDockContext';

import ChatIcon from '@mui/icons-material/Chat';
import type { SxProps, Theme } from '@mui/material';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Fab from '@mui/material/Fab';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { usePreferenceV2 } from 'app-components/flux/preferences-v2';

import AILogoSimpleIcon from 'assets/icons/quicken-ai/ai-logo-simple.inline.svg';
import AIChat from 'components/AIChat';
import { ChatSessionProvider, useChatSession } from 'components/ChatLauncher/chatSession';
import SimplifiAiAssistant from 'containers/SimplifiAiAssistant';
import { randomGreeting } from 'components/AIChat/utils';

const isDev =
  window?.location?.hostname !== 'simplifi.quicken.com' && window?.location?.hostname !== 'app.simplifimoney.com';

interface ChatLauncherProps {
  optBotOffset?: number;
  optRightOffset?: number;
  zIndex?: number;
}

const MinimizeWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isMinimized',
})<{ isMinimized: boolean }>(({ theme, isMinimized }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: 0,
  background: isMinimized ? theme.palette.ai.background : 'transparent',
  borderRadius: isMinimized ? theme.spacing(2.5) : theme.spacing(5),
  boxShadow: isMinimized ? theme.shadows[13] : theme.shadows[1],
  padding: isMinimized ? theme.spacing(0.5) : theme.spacing(0.5),
  transition:
    'background 0.4s cubic-bezier(0.4, 0, 0.2, 1), border-radius 0.4s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.4s cubic-bezier(0.4, 0, 0.2, 1), padding 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  overflow: 'hidden',
  backdropFilter: 'blur(4px)',
  '&:hover': isMinimized
    ? {
        transform: 'translateY(-2px)',
        boxShadow: theme.shadows[15],
        transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      }
    : { transform: 'scale(0.90)', transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)' },
}));

const MinimizedChatTitle = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(1.5),
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  minWidth: 120,
  maxWidth: 200,
  height: 40,
  cursor: 'pointer',
  transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
}));

const ChatLauncherContent = ({
  optBotOffset = 20, // Default value if not provided
  optRightOffset = 20, // Default value if not provided
  zIndex = 1000, // Default value if not provided
}: ChatLauncherProps): JSX.Element | null => {
  const dispatch = useAppDispatch();
  const { prepareConversation, startNewConversation } = useChatSession();
  const launcherStyles: SxProps<Theme> = {
    position: 'fixed',
    bottom: optBotOffset,
    right: optRightOffset,
    zIndex: zIndex,
    alignItems: 'center',
  };

  const anchorEl = useRef<HTMLButtonElement | null>(null);
  const chatDragHandleRef = useRef<HTMLDivElement | null>(null);
  const { isDocked, setIsDocked } = useChatDock();

  const [showAiAssistant, setShowAiAssistant] = useState(false);
  const chatUIState = useAppSelector(llmSelectors.getChatUIState);
  const open = chatUIState !== 'closed';
  const isMinimized = chatUIState === 'minimized';
  const isChatOpen = chatUIState === 'open';

  // Always show "Resume Chat" in minimized view
  const chatTitle = 'Resume Chat';

  // Store greeting in ChatLauncher so it persists across dock/undock (when AIChat remounts)
  const greetingRef = useRef<string | null>(null);
  if (greetingRef.current === null) {
    greetingRef.current = randomGreeting();
  }
  const greeting = greetingRef.current;

  const prevChatUIStateRef = useRef<typeof chatUIState | null>(null);

  React.useEffect(() => {
    const prevState = prevChatUIStateRef.current;

    if (prevState === null) {
      prevChatUIStateRef.current = chatUIState;
      if (chatUIState === 'open') {
        prepareConversation();
      }
      return;
    }

    if (chatUIState === 'open' && prevState !== 'open') {
      prepareConversation();
    }

    if (chatUIState === 'closed' && prevState !== 'closed') {
      startNewConversation();
    }

    prevChatUIStateRef.current = chatUIState;
  }, [chatUIState, prepareConversation, startNewConversation]);

  const ffAiChat = useAppSelector((reduxState) => featureFlagsSelectors.getFeatureFlags(reduxState).get('aiChat'));
  const ffAiChat2 = useAppSelector((reduxState) => featureFlagsSelectors.getFeatureFlags(reduxState).get('aiChatV2'));
  const [AIEnabled] = usePreferenceV2('aiInsightsOptIn');

  // Detect platform for keyboard shortcut display
  const isMac = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const cmdKey = isMac ? '⌘' : 'Ctrl';
  const shiftKey = isMac ? '⇧' : 'Shift';

  // Keyboard shortcut handlers: Command+Shift+O (open/minimize) and Command+. (dock/undock)
  React.useEffect(() => {
    const platformIsMac = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0;

    const handleKeyboardShortcut = (event: KeyboardEvent) => {
      // Check if chat feature is enabled
      if (!ffAiChat2 || !AIEnabled) {
        return;
      }

      // Don't trigger if user is typing in an input field
      const target = event.target as HTMLElement;
      const isInputField =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable ||
        target.closest('input, textarea, [contenteditable="true"]');

      if (isInputField) {
        return;
      }

      const modifierKey = platformIsMac ? event.metaKey : event.ctrlKey;

      // Check for Command+Shift+O (Mac) or Ctrl+Shift+O (Windows/Linux) - Open/Minimize
      const isOpenShortcut = modifierKey && event.shiftKey && event.key.toLowerCase() === 'o';

      // Check for Command+. (Mac) or Ctrl+. (Windows/Linux) - Dock/Undock
      const isDockShortcut = modifierKey && !event.shiftKey && !event.altKey && event.key === '.';

      if (isOpenShortcut) {
        event.preventDefault();
        event.stopPropagation();

        tracker.track(trackingEvents.AIChatClick);

        if (isMinimized) {
          // If minimized, restore it
          dispatch(openChat());
        } else if (isChatOpen && !isDocked) {
          // If open and not docked, minimize it
          dispatch(minimizeChat());
        } else if (isDocked) {
          // If docked, toggle open state
          if (isChatOpen) {
            dispatch(closeChat());
          } else {
            dispatch(openChat());
          }
        } else {
          // If closed, open it
          dispatch(openChat());
        }
      } else if (isDockShortcut) {
        event.preventDefault();
        event.stopPropagation();

        // Toggle dock state
        const newDockedState = !isDocked;
        setIsDocked(newDockedState);
        if (newDockedState) {
          // When docking, ensure it's open
          dispatch(openChat());
        }
      }
    };

    window.addEventListener('keydown', handleKeyboardShortcut);
    return () => {
      window.removeEventListener('keydown', handleKeyboardShortcut);
    };
  }, [dispatch, isChatOpen, isMinimized, isDocked, ffAiChat2, AIEnabled, setIsDocked]);

  const toggleOpen = () => {
    tracker.track(trackingEvents.AIChatClick);
    if (isMinimized) {
      dispatch(openChat());
      return;
    }
    if (isDocked) {
      // If docked, just toggle open state
      if (isChatOpen) {
        dispatch(closeChat());
      } else {
        dispatch(openChat());
      }
      return;
    }
    if (isChatOpen) {
      dispatch(closeChat());
    } else {
      dispatch(openChat());
    }
  };

  const handleClose = () => {
    dispatch(closeChat());
  };

  const handleDock = () => {
    const newDockedState = !isDocked;
    setIsDocked(newDockedState);
    if (newDockedState) {
      // When docking, ensure it's open
      dispatch(openChat());
    }
  };

  if (ffAiChat2 && AIEnabled) {
    const isMinimizedState = isMinimized && !isDocked;
    return (
      <Stack gap={1} sx={launcherStyles}>
        <MinimizeWrapper
          isMinimized={isMinimizedState}
          onClick={
            isMinimizedState
              ? () => {
                  dispatch(openChat());
                }
              : undefined
          }
          sx={isMinimizedState ? { cursor: 'pointer' } : {}}
        >
          <Collapse
            in={isMinimizedState}
            orientation='horizontal'
            timeout={400}
            sx={{
              transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
              minWidth: 0,
            }}
          >
            <MinimizedChatTitle>
              <Typography
                variant='paragraphMedium'
                sx={{
                  color: (theme) => theme.palette.text.secondary,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '100%',
                }}
              >
                {chatTitle}
              </Typography>
            </MinimizedChatTitle>
          </Collapse>
          <Tooltip title={`Ask Quicken Assist (${cmdKey}${shiftKey}O)`} placement='left'>
            <Fab
              ref={anchorEl}
              onClick={toggleOpen}
              size='small'
              aria-label='ai-chat'
              sx={(theme) => ({
                background: theme.palette.ai.gradientRadial,
                color: theme.palette.common.white,
                transition: 'transform 0.2s ease-in-out',
                position: 'relative',
                zIndex: 1,
                '&:hover': {
                  transform: 'scale(0.90)',
                  transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                },
              })}
            >
              <AILogoSimpleIcon />
            </Fab>
          </Tooltip>
        </MinimizeWrapper>
        <DraggablePopper
          isMinimized={isMinimized}
          isDocked={isDocked}
          anchorEl={anchorEl.current}
          open={open}
          placement='top'
          dragHandleRef={chatDragHandleRef}
        >
          <AIChat
            handleMinimize={() => dispatch(minimizeChat())}
            handleDock={handleDock}
            isDocked={isDocked}
            onClose={handleClose}
            dragHandleRef={chatDragHandleRef}
            greeting={greeting}
          />
        </DraggablePopper>
      </Stack>
    );
  }
  if (isDev && ffAiChat) {
    return (
      <>
        <SimplifiAiAssistant
          visible={showAiAssistant}
          onClose={() => {
            setShowAiAssistant(false);
          }}
        />
        <Fab
          color='primary'
          aria-label='chat'
          onClick={() => {
            setShowAiAssistant(!showAiAssistant);
          }}
          sx={launcherStyles}
        >
          <ChatIcon />
        </Fab>
      </>
    );
  }
  return null;
};

const ChatLauncher = (props: ChatLauncherProps): JSX.Element => {
  return (
    <ChatSessionProvider>
      <ChatLauncherContent {...props} />
    </ChatSessionProvider>
  );
};

export default ChatLauncher;
export { ChatDockProvider, useChatDock } from './ChatDockContext';
